'use client';

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Info } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/layout/popover';
import { cn } from '@/lib/utils';

interface InfoPopoverProps {
  content: ReactNode;
  ariaLabel?: string;
  triggerClassName?: string;
  iconClassName?: string;
  contentClassName?: string;
  align?: 'start' | 'center' | 'end';
  sideOffset?: number;
  /** Custom trigger element rendered in place of the default Info icon. The same
   *  hover + click open behavior is applied to it. */
  trigger?: ReactNode;
}

const HOVER_CLOSE_DELAY_MS = 120;

export function InfoPopover({
  content,
  ariaLabel = 'Show details',
  triggerClassName,
  iconClassName,
  contentClassName,
  align = 'end',
  sideOffset = 6,
  trigger,
}: InfoPopoverProps) {
  const [isPinnedOpen, setIsPinnedOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const handleHoverStart = useCallback(() => {
    clearCloseTimer();
    setIsHovered(true);
  }, [clearCloseTimer]);

  const handleHoverEnd = useCallback(() => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      setIsHovered(false);
    }, HOVER_CLOSE_DELAY_MS);
  }, [clearCloseTimer]);

  const handleOpenChange = useCallback((nextOpen: boolean) => {
    if (!nextOpen) {
      setIsPinnedOpen(false);
      setIsHovered(false);
    }
  }, []);

  useEffect(() => () => clearCloseTimer(), [clearCloseTimer]);

  const isOpen = isPinnedOpen || isHovered;

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        {trigger ? (
          <span
            role="button"
            tabIndex={0}
            aria-label={ariaLabel}
            className={cn(
              'inline-flex cursor-help items-center outline-none focus:outline-none focus-visible:outline-none focus-visible:ring-0',
              triggerClassName
            )}
            onClick={(event) => {
              event.stopPropagation();
              clearCloseTimer();
              setIsPinnedOpen((prev) => !prev);
            }}
            onMouseEnter={handleHoverStart}
            onMouseLeave={handleHoverEnd}
          >
            {trigger}
          </span>
        ) : (
          <button
            type="button"
            aria-label={ariaLabel}
            className={cn(
              'inline-flex h-6 w-6 cursor-pointer items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground',
              triggerClassName
            )}
            onClick={(event) => {
              event.stopPropagation();
              clearCloseTimer();
              setIsPinnedOpen((prev) => !prev);
            }}
            onMouseEnter={handleHoverStart}
            onMouseLeave={handleHoverEnd}
          >
            <Info className={cn('h-3.5 w-3.5', iconClassName)} />
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent
        align={align}
        sideOffset={sideOffset}
        // Don't yank focus back to the trigger when a hover-opened popover closes
        // — that's what left a lingering focus ring ("burgundy box") on the trigger.
        onCloseAutoFocus={(event) => event.preventDefault()}
        className={cn(
          'z-50 w-64 rounded-md border border-border bg-popover p-2 text-popover-foreground shadow-lg',
          contentClassName
        )}
        onMouseEnter={handleHoverStart}
        onMouseLeave={handleHoverEnd}
      >
        <div className="text-xs leading-relaxed">{content}</div>
      </PopoverContent>
    </Popover>
  );
}

